// src/components/Widgets/ProductionWidget.jsx
import React from "react";
import PropTypes from "prop-types";
import WidgetHeader from "../Common/WidgetHeader";

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

const ProductionWidget = ({
  batches = [],
  monthlyTarget = 0,
  onCreateBatch = () => {},
  onViewAll = () => {},
  backTo = "/dashboard",
}) => {
  const sorted = Array.isArray(batches) ? [...batches] : [];
  if (sorted.length && sorted[0]?.date) {
    sorted.sort((a, b) => new Date(b.date) - new Date(a.date));
  }

  const totalInProgress = sorted.filter((b) => b?.status === "In Progress").length;
  const totalCompleted  = sorted.filter((b) => b?.status === "Completed").length;
  const totalDelayed    = sorted.filter((b) => b?.status === "Delayed").length;

  const target = Number(monthlyTarget) || 0;
  const done   = totalCompleted;
  const pct    = target > 0 ? clamp(Math.round((done / target) * 100), 0, 100) : 0;

  return (
    // match other widgets: transparent, inherited text, full-height
    <div className="rounded-lg p-4 h-full flex flex-col bg-transparent text-inherit">
      <WidgetHeader title="Production Overview" to={backTo} />

      {/* Progress vs target */}
      <div className="space-y-1">
        <div className="flex items-baseline justify-between text-xs tabular-nums">
          <span className="opacity-70">
            Target: <span className="opacity-100 font-semibold">{target}</span>
          </span>
          <span className="opacity-70">
            Completed: <span className="opacity-100 font-semibold">{done}</span>
          </span>
          <span className="opacity-70">
            Progress: <span className="opacity-100 font-semibold">{pct}%</span>
          </span>
        </div>
        <div
          className="h-2 w-full rounded bg-white/10 overflow-hidden"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Monthly production progress"
        >
          <div
            className="h-full bg-emerald-500"
            style={{ width: `${pct}%`, transition: "width 300ms ease" }}
          />
        </div>
      </div>

      {/* Counters */}
      <div className="text-sm grid grid-cols-3 gap-4 tabular-nums">
        <p>
          <span className="opacity-70">In Progress: </span>
          <span className="font-semibold text-orange-300">{totalInProgress}</span>
        </p>
        <p>
          <span className="opacity-70">Completed: </span>
          <span className="font-semibold text-green-400">{totalCompleted}</span>
        </p>
        <p>
          <span className="opacity-70">Delayed: </span>
          <span className="font-semibold text-red-500">{totalDelayed}</span>
        </p>
      </div>

      {/* List / empty state */}
      <div className="mt-1 flex-1">
        {sorted.length > 0 ? (
          <div className="border-t border-white/10 pt-3 text-sm space-y-2 max-h-40 overflow-y-auto">
            {sorted.map((batch, i) => {
              const name = batch?.name || "Untitled batch";
              const status = batch?.status || "Not Started";
              const notes = batch?.notes;

              return (
                <div key={batch?.id ?? i} className="flex justify-between items-center">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{name}</p>
                    {notes && <p className="opacity-60 text-xs truncate">{notes}</p>}
                  </div>
                  <span
                    className={`px-2 py-1 text-xs rounded border border-white/10 ${
                      status === "Completed"
                        ? "bg-green-600/30 text-green-300"
                        : status === "Delayed"
                        ? "bg-red-600/30 text-red-300"
                        : status === "In Progress"
                        ? "bg-yellow-400/70 text-black"
                        : "bg-white/10"
                    }`}
                  >
                    {status}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="italic opacity-70">
            No batches yet.{" "}
            <button onClick={onCreateBatch} className="text-amber-400 hover:underline">
              Create a Batch
            </button>
          </p>
        )}
      </div>

      {/* Footer actions (match other widgets) */}
      <div
        className="pt-2 mt-2 flex items-center justify-end gap-2"
        style={{ borderTop: "1px solid color-mix(in oklab, currentColor 12%, transparent)" }}
      >
        <button
          onClick={onViewAll}
          className="px-3 py-1.5 rounded border border-white/10 hover:bg-white/10 text-sm"
          title="Open all batches"
        >
          View all
        </button>
        <button
          onClick={onCreateBatch}
          className="px-3 py-1.5 rounded bg-amber-500 hover:bg-amber-600 text-black text-sm font-medium"
        >
          Create batch
        </button>
      </div>
    </div>
  );
};

ProductionWidget.propTypes = {
  batches: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      status: PropTypes.oneOf(["In Progress", "Completed", "Delayed", "Not Started"]),
      notes: PropTypes.string,
      date: PropTypes.string,
    })
  ),
  monthlyTarget: PropTypes.number,
  onCreateBatch: PropTypes.func,
  onViewAll: PropTypes.func,
  backTo: PropTypes.string,
};

export default ProductionWidget;
